import { useField } from "formik"
import { makeStyles } from "tss-react/mui"
import { Stack, Typography } from "@mui/material"
import { SelectedPriceListItem, CreateOrderDialogMode, CreateOrderFlowType } from "../../../../../createOrderTypes"
import { useCreateOrderFormConfig, useCreateOrderFormState } from "../../../../../mainForm/CreateOrderFormContext"
import { useCreateOrderDialogType } from "../../../../../hooks/useCreateOrderDialogType"
import { DeadlineMessageQuery, DeadlineMessageResponse, fetchDeadlineMessage } from "../../deliveryApi"
import { LocalizedStrict } from "../../../../../../../../modules/localization/components/AppLocalized"
import { useDateTime } from "../../../../../../../../modules/localization/useDateTime"
import { CreateUpdateDeliveryFormValues } from "../../CreateUpdateDeliveryTimePopover"
import { FormikDatePickerControlled } from "../../../../../../forms/Formik"
import { useApiQuery } from "../../../../../../../../core/api/useApiQuery"
import { validateDeliveryDate } from "../../helpers"
import { CustomDay } from "./CustomDay"

const useStyles = makeStyles()((theme) => ({
    message: {
        borderRadius: ".5rem",
        padding: ".75rem 1rem",
        fontWeight: "500",
    },
    messageInfo: {
        color: theme.palette.tailwind.blue[950],
        background: theme.palette.tailwind.sky[100],
    },
    messageWarning: {
        color: theme.palette.tailwind.red[950],
        background: theme.palette.tailwind.red[100],
    },
    subtitle: {
        color: theme.palette.tailwind.slate[700],
    },
    subtitleDetails: {
        display: "inline-block",
    },
}))

const transformData = (data: DeadlineMessageResponse) => {
    return data.deadlineMessage
}

type DeliveryDatePickerProps = Readonly<{
    closingDates: string[]
    priceList: SelectedPriceListItem
    allowedWeekdays: number[]
    disabledDatePicker: boolean
    isShowTimeWarningMessage: boolean
    allowPastDates: boolean
}>

export function DeliveryDatePicker({
    closingDates,
    priceList,
    allowedWeekdays,
    disabledDatePicker,
    isShowTimeWarningMessage,
    allowPastDates,
}: DeliveryDatePickerProps) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()
    const { flowConfig } = useCreateOrderFormConfig()
    const { orderContext } = useCreateOrderFormState()
    const { isRegularOrder } = useCreateOrderDialogType()
    const canEditDeliveryDate = isRegularOrder && orderContext.mode === CreateOrderDialogMode.EDIT

    const [{ value: deliveryDate }] = useField<CreateUpdateDeliveryFormValues["deliveryDate"]>("deliveryDate")

    const selectedTimes = deliveryDate && dateTimeFactory.toApi(deliveryDate)
    const { isValidDeliveryDate } = validateDeliveryDate(deliveryDate, allowedWeekdays, closingDates)

    const { data } = useApiQuery(fetchDeadlineMessage, {
        queryName: DeadlineMessageQuery.DELIVERY_DEADLINE_MESSAGE,
        dependencies: {
            merchantId: priceList.merchantId,
            deliveryTime: selectedTimes!,
            priceListId: priceList.priceListId,
            shopId: priceList.shopId,
        },
        select: transformData,
        enabled: !!selectedTimes && !isShowTimeWarningMessage && isValidDeliveryDate,
    })

    const isDisabledDatePicker =
        (!canEditDeliveryDate && flowConfig?.flow === CreateOrderFlowType.OUTLOOK && !!deliveryDate) ||
        disabledDatePicker

    return (
        <Stack gap=".75rem">
            {isDisabledDatePicker ? (
                <>
                    <Typography variant="body1" className={classes.subtitle}>
                        <LocalizedStrict id="create-order-delivery-times-dialog-delivery-date-details">
                            <Typography className={classes.subtitleDetails}>Delivery date:</Typography>
                        </LocalizedStrict>{" "}
                        <Typography className={classes.subtitleDetails} fontWeight={700}>
                            {deliveryDate ? dateTimeFactory.formatDate(deliveryDate) : ""}
                        </Typography>
                    </Typography>
                    {!isValidDeliveryDate && (
                        <LocalizedStrict id="create-order-delivery-times-dialog-delivery-date-close-tooltip">
                            <Typography variant="body2" className={cx(classes.message, classes.messageWarning)}>
                                Merchant is closed on this date
                            </Typography>
                        </LocalizedStrict>
                    )}
                </>
            ) : (
                <LocalizedStrict id="create-order-delivery-times-dialog-delivery-date">
                    <Typography variant="body1" className={classes.subtitle}>
                        Delivery date
                    </Typography>
                </LocalizedStrict>
            )}
            <Stack gap=".75rem">
                {!isDisabledDatePicker && (
                    <FormikDatePickerControlled
                        name="deliveryDate"
                        slotProps={{ textField: { size: "small", fullWidth: true } }}
                        slots={{
                            day: (props) => (
                                <CustomDay
                                    {...props}
                                    closingDates={closingDates}
                                    allowedWeekdays={allowedWeekdays}
                                    allowPastDates={allowPastDates}
                                />
                            ),
                        }}
                        disablePast={!allowPastDates}
                        disabled={!canEditDeliveryDate && flowConfig?.flow === CreateOrderFlowType.OUTLOOK}
                    />
                )}
                {data?.message?.displayRule && isValidDeliveryDate && (
                    <Typography variant="body2" className={cx(classes.message, classes.messageInfo)}>
                        {data.message.displayRule}
                    </Typography>
                )}
            </Stack>
        </Stack>
    )
}
